import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  ShoppingCart as OrderIcon,
  CheckCircle as CompletedIcon,
  PendingActions as PendingIcon,
  Cancel as CancelledIcon,
  LocalShipping as ShippingIcon,
  Receipt as ReceiptIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";

const MyOrders = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Mock orders data - Replace with API call
  const orders = [
    {
      id: "ORD-2024-001",
      schoolName: "Greenwood High School",
      orderDate: "2024-01-10",
      items: [
        { name: "EduOK Premium License", quantity: 1, price: 45000 },
        { name: "Training Package", quantity: 2, price: 15000 },
      ],
      totalAmount: 75000,
      status: "Completed",
      paymentStatus: "Paid",
      deliveryDate: "2024-01-15",
      invoiceNumber: "INV-2024-001",
    },
    {
      id: "ORD-2024-002",
      schoolName: "Sunshine Academy",
      orderDate: "2024-01-12",
      items: [
        { name: "EduOK Basic License", quantity: 1, price: 25000 },
      ],
      totalAmount: 25000,
      status: "Pending",
      paymentStatus: "Pending",
      deliveryDate: null,
      invoiceNumber: null,
    },
    {
      id: "ORD-2024-003",
      schoolName: "Riverside School",
      orderDate: "2024-01-08",
      items: [
        { name: "EduOK Premium License", quantity: 1, price: 45000 },
        { name: "Support Package", quantity: 1, price: 10000 },
      ],
      totalAmount: 55000,
      status: "Shipped",
      paymentStatus: "Paid",
      deliveryDate: "2024-01-20",
      invoiceNumber: "INV-2024-003",
    },
    {
      id: "ORD-2024-004",
      schoolName: "Mountain View School",
      orderDate: "2024-01-05",
      items: [
        { name: "EduOK Enterprise License", quantity: 1, price: 85000 },
      ],
      totalAmount: 85000,
      status: "Cancelled",
      paymentStatus: "Refunded",
      deliveryDate: null,
      invoiceNumber: "INV-2024-004",
    },
  ];

  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.schoolName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.invoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "success";
      case "Pending":
        return "warning";
      case "Shipped":
        return "info";
      case "Cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Completed":
        return <CompletedIcon />;
      case "Pending":
        return <PendingIcon />;
      case "Shipped":
        return <ShippingIcon />;
      case "Cancelled":
        return <CancelledIcon />;
      default:
        return <OrderIcon />;
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setOrderDialogOpen(true);
  };

  const totalRevenue = orders
    .filter((o) => o.status === "Completed")
    .reduce((sum, order) => sum + order.totalAmount, 0);

  const pendingOrders = orders.filter((o) => o.status === "Pending").length;

  return (
    <Box>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              My Orders
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              Track and manage all your orders
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => window.location.reload()}
          >
            Refresh
          </Button>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                  Total Orders
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                  {orders.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                  Total Revenue
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                  ₹{totalRevenue.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                  Pending Orders
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.warning.main }}>
                  {pendingOrders}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                  Completed Orders
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                  {orders.filter((o) => o.status === "Completed").length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
              <TextField
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flexGrow: 1, minWidth: 250 }}
              />
              <Button
                variant={statusFilter !== "all" ? "contained" : "outlined"}
                startIcon={<FilterIcon />}
                onClick={(e) => setFilterAnchor(e.currentTarget)}
              >
                {statusFilter === "all" ? "All Status" : statusFilter}
              </Button>
              <Menu
                anchorEl={filterAnchor}
                open={Boolean(filterAnchor)}
                onClose={() => setFilterAnchor(null)}
              >
                <MenuItem onClick={() => { setStatusFilter("all"); setFilterAnchor(null); }}>
                  All Status
                </MenuItem>
                <MenuItem onClick={() => { setStatusFilter("Completed"); setFilterAnchor(null); }}>
                  Completed
                </MenuItem>
                <MenuItem onClick={() => { setStatusFilter("Pending"); setFilterAnchor(null); }}>
                  Pending
                </MenuItem>
                <MenuItem onClick={() => { setStatusFilter("Shipped"); setFilterAnchor(null); }}>
                  Shipped
                </MenuItem>
                <MenuItem onClick={() => { setStatusFilter("Cancelled"); setFilterAnchor(null); }}>
                  Cancelled
                </MenuItem>
              </Menu>
            </Box>
          </CardContent>
        </Card>

        {/* Orders Table */}
        <Card>
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Order ID</TableCell>
                    <TableCell>School</TableCell>
                    <TableCell>Order Date</TableCell>
                    <TableCell>Items</TableCell>
                    <TableCell>Total Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Payment</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOrders.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          No orders found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredOrders.map((order) => (
                      <TableRow key={order.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: "monospace" }}>
                            {order.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {order.schoolName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{order.orderDate}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {order.items.length} item(s)
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                            ₹{order.totalAmount.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(order.status)}
                            label={order.status}
                            size="small"
                            color={getStatusColor(order.status)}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={order.paymentStatus}
                            size="small"
                            color={order.paymentStatus === "Paid" ? "success" : "warning"}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => handleViewOrder(order)}
                            color="primary"
                          >
                            <ViewIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Order Details Dialog */}
        <Dialog open={orderDialogOpen} onClose={() => setOrderDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Order Details - {selectedOrder?.id}
          </DialogTitle>
          <DialogContent>
            {selectedOrder && (
              <Box>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 0.5 }}>
                      School Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedOrder.schoolName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 0.5 }}>
                      Order Date
                    </Typography>
                    <Typography variant="body1">
                      {selectedOrder.orderDate}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 0.5 }}>
                      Status
                    </Typography>
                    <Chip
                      icon={getStatusIcon(selectedOrder.status)}
                      label={selectedOrder.status}
                      color={getStatusColor(selectedOrder.status)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 0.5 }}>
                      Payment Status
                    </Typography>
                    <Chip
                      label={selectedOrder.paymentStatus}
                      color={selectedOrder.paymentStatus === "Paid" ? "success" : "warning"}
                    />
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Order Items
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrder.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">₹{item.price.toLocaleString()}</TableCell>
                          <TableCell align="right">₹{(item.quantity * item.price).toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={3} align="right" sx={{ fontWeight: 600 }}>
                          Grand Total:
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
                          ₹{selectedOrder.totalAmount.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                {selectedOrder.invoiceNumber && (
                  <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<ReceiptIcon />}
                      fullWidth
                    >
                      View Invoice
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      fullWidth
                    >
                      Download Invoice
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOrderDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </motion.div>
    </Box>
  );
};

export default MyOrders;

